import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

import { getAuthHeaders } from './authHeaders';

const useReferralsData = ({ show, editingNote }) => {
    const [referrals, setReferrals] = useState([]);
    const [referralTo, setReferralTo] = useState('');
    const [referralContent, setReferralContent] = useState('');
    const [loadingReferrals, setLoadingReferrals] = useState(false);
    const [savingReferral, setSavingReferral] = useState(false);
    const [editingReferralId, setEditingReferralId] = useState(null);
    const [referralsToDelete, setReferralsToDelete] = useState([]);

    const [uniqueReferralTos, setUniqueReferralTos] = useState([]);
    const [isLoadingUniqueReferralTos, setIsLoadingUniqueReferralTos] = useState(false);
    const [isReferralDropdownOpen, setIsReferralDropdownOpen] = useState(false);
    const referralWrapperRef = useRef(null);

    useEffect(() => {
        const fetchUniqueReferralTos = async () => {
            try {
                setIsLoadingUniqueReferralTos(true);
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}referrals/unique-to`,
                    {
                        headers: getAuthHeaders()
                    }
                );
                setUniqueReferralTos(Array.isArray(res.data) ? res.data : []);
            } catch (err) {
                console.error('Error fetching unique referral "to" values:', err);
            } finally {
                setIsLoadingUniqueReferralTos(false);
            }
        };

        if (show) {
            fetchUniqueReferralTos();
        }
    }, [show]);

    useEffect(() => {
        const fetchReferrals = async () => {
            if (!editingNote?.id) return;
            try {
                setLoadingReferrals(true);
                const patientId = editingNote.patient?.id || editingNote.patient_id;
                if (!patientId) return;

                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}referrals/note/${editingNote.id}`,
                    {
                        headers: getAuthHeaders()
                    }
                );
                setReferrals(Array.isArray(res.data) ? res.data : []);
            } catch (err) {
                console.error('Error fetching referrals:', err);
                setReferrals([]);
            } finally {
                setLoadingReferrals(false);
            }
        };

        if (show) fetchReferrals();
    }, [show, editingNote]);

    const handleSaveReferral = () => {
        if (!referralTo.trim() || !referralContent.trim()) return;
        setSavingReferral(true);
        if (editingReferralId) {
            setReferrals(prev => prev.map(r => (
                r.id === editingReferralId
                    ? { ...r, to: referralTo.trim(), content: referralContent.trim() }
                    : r
            )));
        } else {
            setReferrals(prev => [
                { to: referralTo.trim(), content: referralContent.trim() },
                ...prev,
            ]);
        }

        setReferralTo('');
        setReferralContent('');
        setEditingReferralId(null);
        setSavingReferral(false);
    };

    const handleEditReferral = (ref) => {
        setReferralTo(ref.to || '');
        setReferralContent(ref.content || '');
        setEditingReferralId(ref.id);
    };

    const handleCancelEditReferral = () => {
        setEditingReferralId(null);
        setReferralTo('');
        setReferralContent('');
    };

    const handleDeleteReferral = (id) => {
        setReferrals(prev => prev.filter(r => r.id !== id));
        if (id) {
            setReferralsToDelete(prev => (prev.includes(id) ? prev : [...prev, id]));
        }
        if (editingReferralId === id) {
            setEditingReferralId(null);
            setReferralTo('');
            setReferralContent('');
        }
    };

    return {
        referrals,
        setReferrals,
        referralTo,
        setReferralTo,
        referralContent,
        setReferralContent,
        loadingReferrals,
        savingReferral,
        editingReferralId,
        setEditingReferralId,
        referralsToDelete,
        uniqueReferralTos,
        isLoadingUniqueReferralTos,
        isReferralDropdownOpen,
        setIsReferralDropdownOpen,
        referralWrapperRef,
        handleSaveReferral,
        handleEditReferral,
        handleCancelEditReferral,
        handleDeleteReferral,
    };
};

export default useReferralsData;
